import React, { useState, useEffect } from 'react';
import { contactApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';

export default function ContactModal({ open, onClose, defaultSubject = '' }) {
    const [form, setForm] = useState({ name: '', email: '', phone: '', subject: defaultSubject, message: '' });
    const [status, setStatus] = useState('idle'); // idle | sending | success | error
    const [errorMsg, setErrorMsg] = useState('');

    // Update subject when defaultSubject prop changes (e.g., when opening from different hardware enquire buttons)
    useEffect(() => {
        if (open) {
            setForm(prev => ({ ...prev, subject: defaultSubject }));
        }
    }, [defaultSubject, open]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        setErrorMsg('');
        try {
            await contactApi.submit(form);
            setStatus('success');
        } catch (err) {
            setErrorMsg(err.response?.data?.detail || 'Failed to send. Please try again.');
            setStatus('error');
        }
    };

    const reset = () => {
        setForm({ name: '', email: '', phone: '', subject: '', message: '' });
        setStatus('idle');
        setErrorMsg('');
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="contact-modal-overlay">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={reset} />
            <div className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl" data-testid="contact-modal">
                <button onClick={reset} className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors" data-testid="contact-modal-close">
                    <X className="h-5 w-5" />
                </button>

                {status === 'success' ? (
                    <div className="p-8 text-center" data-testid="contact-success">
                        <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-100 mb-2">Enquiry Sent!</h3>
                        <p className="text-sm text-slate-400 mb-6">We'll get back to you within 24 hours.</p>
                        <Button onClick={reset} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium" data-testid="contact-done-btn">Done</Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6">
                        <h3 className="text-lg font-semibold text-slate-100 mb-1">Get in Touch</h3>
                        <p className="text-sm text-slate-400 mb-5">We'll reply within 24 hours.</p>

                        <div className="space-y-3">
                            <div>
                                <Label className="text-slate-300 text-xs">Name *</Label>
                                <Input name="name" value={form.name} onChange={handleChange} required
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100 h-9" placeholder="Your name" data-testid="contact-name" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-slate-300 text-xs">Email *</Label>
                                    <Input name="email" type="email" value={form.email} onChange={handleChange} required
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100 h-9" placeholder="you@email.com" data-testid="contact-email" />
                                </div>
                                <div>
                                    <Label className="text-slate-300 text-xs">Phone</Label>
                                    <Input name="phone" value={form.phone} onChange={handleChange}
                                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100 h-9" placeholder="Optional" data-testid="contact-phone" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-slate-300 text-xs">Subject *</Label>
                                <Input name="subject" value={form.subject} onChange={handleChange} required
                                    className="mt-1 bg-slate-800 border-slate-700 text-slate-100 h-9" placeholder="e.g. Hardware Enquiry" data-testid="contact-subject" />
                            </div>
                            <div>
                                <Label className="text-slate-300 text-xs">Message *</Label>
                                <textarea name="message" value={form.message} onChange={handleChange} required rows={3}
                                    className="mt-1 w-full rounded-md bg-slate-800 border border-slate-700 text-slate-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                                    placeholder="How can we help?" data-testid="contact-message" />
                            </div>
                        </div>

                        {errorMsg && <p className="text-xs text-rose-400 mt-2" data-testid="contact-error">{errorMsg}</p>}

                        <Button type="submit" disabled={status === 'sending'}
                            className="w-full mt-5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold h-10"
                            data-testid="contact-submit-btn">
                            {status === 'sending' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                            {status === 'sending' ? 'Sending...' : 'Send Enquiry'}
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
